import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap, Search, Settings, Plus, Bookmark, Lock, Hash, Users,
  ChevronRight, Check, X, UserPlus, MessageCircle, User, Radio, ArrowLeft,
} from 'lucide-react';
import clsx from 'clsx';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelsApi } from '@/api/channels';
import { friendsApi } from '@/api/friends';
import { botsApi } from '@/api/bots';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import { useMobile } from '@/pages/chat/ChatPage';
import Avatar from '@/components/ui/Avatar';
import NickName from '@/components/ui/NickName';
import StoryTray from '@/components/stories/StoryTray';
import ForYouFeed from '@/components/stories/ForYouFeed';
import GlobalSearchModal from './GlobalSearchModal';
import NotificationPanel from './NotificationPanel';
import CreateChannelModal from './CreateChannelModal';

/**
 * MobileShell — главный мобильный экран, 1-в-1 повторяющий Android
 * ChannelListScreen.kt: верхний бар «SWIPA» (56dp, Surface900) с экшенами
 * ⚡/🔴/🔍/🔔/⚙, TabRow «Каналы | Друзья(+бейдж)» (48dp, brand-underline),
 * StoryTray на табе каналов, список каналов Android-строками (64dp, аватар
 * 48dp, chevron), вкладка друзей (заявки + presence), футер «Мой профиль»,
 * FAB создания канала. Нижней навигации в Android нет — здесь тоже нет.
 * Рендерится только на мобильном (обёртка md:hidden в ChatPage).
 */
export default function MobileShell({ onBackToHub }: { onBackToHub?: () => void }) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { setShowSidebar } = useMobile();
  const [tab, setTab] = useState<'channels' | 'friends'>('channels');
  const [showForYou, setShowForYou] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: pendingRequests } = useQuery({
    queryKey: ['friends', 'pending'],
    queryFn: () => friendsApi.listPending(),
    staleTime: 30_000,
  });
  const pendingCount = pendingRequests?.length ?? 0;

  const openChat = (path: string) => {
    setShowSidebar(false);
    navigate(path);
  };

  return (
    <div className="flex flex-col h-full w-full bg-surface-950">
      {/* ── Top app bar (TopAppBar 56dp, Surface900) ── */}
      <div className="an-topbar">
        {/* На Android назад в Hub ведёт системная кнопка; на iOS её нет — стрелка */}
        {onBackToHub && (
          <button className="an-topbar__action -ml-2" onClick={onBackToHub} aria-label="В меню">
            <ArrowLeft size={24} className="text-surface-200" />
          </button>
        )}
        <span className="an-topbar__title an-topbar__title--brand">SWIPA</span>
        <div className="flex-1" />
        <button className="an-topbar__action" style={{ color: '#7c3aed' }} onClick={() => setShowForYou(true)} aria-label="Для вас">
          <Zap size={24} />
        </button>
        <button className="an-topbar__action" style={{ color: '#ff3b30' }} onClick={() => setShowForYou(true)} aria-label="Эфиры">
          <Radio size={24} />
        </button>
        <button className="an-topbar__action" onClick={() => setShowSearch(true)} aria-label="Поиск">
          <Search size={24} />
        </button>
        {/* Колокольчик — существующая самодостаточная кнопка с попапом */}
        <NotificationPanel />
        <button className="an-topbar__action" onClick={() => openChat('/settings')} aria-label="Настройки">
          <Settings size={24} />
        </button>
      </div>

      {/* ── Tab row (48dp, активный brand + underline 2dp) ── */}
      <div className="an-tabrow">
        <button className={clsx('an-tab', tab === 'channels' && 'an-tab--active')} onClick={() => setTab('channels')}>
          Каналы
        </button>
        <button className={clsx('an-tab', tab === 'friends' && 'an-tab--active')} onClick={() => setTab('friends')}>
          Друзья
          {pendingCount > 0 && <span className="an-badge">{pendingCount}</span>}
        </button>
      </div>

      {/* ── Story tray — только на табе каналов (как в Android) ── */}
      {tab === 'channels' && (
        <div className="bg-surface-900 border-b border-surface-800">
          <StoryTray />
        </div>
      )}

      {/* ── Контент таба ── */}
      <div className="flex-1 overflow-y-auto relative">
        {tab === 'channels' ? <ChannelsTab onOpen={openChat} /> : <FriendsTab onOpen={openChat} />}

        {/* FAB — только на табе каналов */}
        {tab === 'channels' && (
          <button
            className="an-fab fixed right-4 z-30"
            style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)' }}
            onClick={() => setCreateOpen(true)}
            aria-label="Создать канал"
          >
            <Plus size={24} />
          </button>
        )}
      </div>

      {/* ── Футер «Мой профиль» (строка 16dp + divider, как в Android) ── */}
      <div
        className="flex items-center gap-3 px-4 py-4 border-t border-surface-800 bg-surface-950 cursor-pointer"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
        onClick={() => openChat(`/u/${user?.username}`)}
      >
        <User size={20} className="text-brand-600" style={{ color: '#7c3aed' }} />
        <span className="text-[15px] text-surface-200">Мой профиль</span>
      </div>

      {/* ── Оверлеи ── */}
      {showForYou && (
        <div className="fixed inset-0 z-50 bg-black">
          <button
            onClick={() => setShowForYou(false)}
            className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
            aria-label="Закрыть"
          >
            ✕
          </button>
          <ForYouFeed />
        </div>
      )}
      {showSearch && <GlobalSearchModal onClose={() => setShowSearch(false)} />}
      <CreateChannelModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

/* ── Таб «Каналы» — ChannelsTab из ChannelListScreen.kt ─────────────────── */

function ChannelsTab({ onOpen }: { onOpen: (path: string) => void }) {
  const { joinedChannels, unreadCounts, setActiveChannel, addJoinedChannel } = useChatStore();
  const [search, setSearch] = useState('');

  const { data: publicChannels } = useQuery({
    queryKey: ['channels', 'public', search],
    queryFn: () => channelsApi.list({ limit: 30, search: search || undefined }),
    enabled: search.length >= 1,
  });

  // Личный «Сохранённые» первым — как sortedByDescending { it.isPersonal }
  const sorted = useMemo(
    () => [...(joinedChannels ?? [])].sort((a, b) => Number(b.isPersonal ?? false) - Number(a.isPersonal ?? false)),
    [joinedChannels],
  );

  const openChannel = (id: string) => {
    setActiveChannel(id);
    onOpen(`/channel/${id}`);
  };

  return (
    <div className="pb-24">
      {/* Поиск (12dp отступы, rounded 12, Surface900, border Surface800) */}
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 bg-surface-900 border border-surface-800 focus-within:border-brand-500 rounded-xl px-3 py-2.5 transition-colors">
          <Search size={18} className="text-surface-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск каналов…"
            className="bg-transparent text-sm text-surface-50 outline-none w-full placeholder:text-surface-400"
          />
          {search.length > 0 && (
            <button onClick={() => setSearch('')} className="text-surface-400" aria-label="Очистить">
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      {search.length > 0 ? (
        /* Результаты поиска публичных каналов: строка + действие */
        <>
          {publicChannels?.data.length === 0 && (
            <p className="px-4 py-6 text-surface-400 text-sm text-center">Каналы не найдены</p>
          )}
          {publicChannels?.data.map((ch) => (
            <ChannelRow
              key={ch.id}
              icon={ch.type === 'PRIVATE' ? 'lock' : 'tag'}
              title={ch.displayName || ch.name}
              subtitle={`${ch._count?.members ?? 0} участников`}
              onClick={async () => {
                if (ch.type === 'PRIVATE') {
                  const res = await channelsApi.requestJoin(ch.id);
                  if (res.status === 'pending') alert('Заявка отправлена');
                } else {
                  await channelsApi.join(ch.id);
                  addJoinedChannel(ch);
                  openChannel(ch.id);
                  setSearch('');
                }
              }}
            />
          ))}
        </>
      ) : (
        <>
          {sorted.length === 0 && (
            <p className="px-4 py-8 text-surface-400 text-sm text-center">
              Вы не состоите ни в одном канале.<br />Введите название в поиске.
            </p>
          )}
          {sorted.map((ch) => {
            const unread = unreadCounts[ch.id] ?? 0;
            const memberCount = ch._count?.members;
            return (
              <ChannelRow
                key={ch.id}
                icon={ch.isPersonal ? 'bookmark' : ch.type === 'PRIVATE' ? 'lock' : 'tag'}
                title={ch.isPersonal ? 'Сохранённые' : ch.displayName || ch.name}
                subtitle={
                  ch.isPersonal
                    ? 'Личные заметки'
                    : ch.topic
                      ? `ТЕМА: ${ch.topic}`
                      : memberCount !== undefined
                        ? `${memberCount} участников`
                        : ''
                }
                unread={unread}
                memberCount={!unread ? memberCount : undefined}
                onClick={() => openChannel(ch.id)}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

/** Строка канала: ChannelItem — 64dp, аватар 48dp brand-кружок, chevron справа */
function ChannelRow({
  icon, title, subtitle, unread, memberCount, onClick,
}: {
  icon: 'bookmark' | 'lock' | 'tag';
  title: string;
  subtitle?: string;
  unread?: number;
  memberCount?: number;
  onClick: () => void;
}) {
  const IconCmp = icon === 'bookmark' ? Bookmark : icon === 'lock' ? Lock : Hash;
  return (
    <>
      <button className="an-list-row" onClick={onClick}>
        <div className="an-avatar an-avatar--48">
          <IconCmp size={22} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="an-list-row__title truncate">{title}</p>
          {subtitle ? <p className="an-list-row__sub truncate">{subtitle}</p> : null}
        </div>
        {unread ? (
          <span className="an-badge">{unread > 99 ? '99+' : unread}</span>
        ) : memberCount !== undefined ? (
          <span className="flex items-center gap-1 text-xs text-surface-700">
            <Users size={14} />
            {memberCount > 999 ? `${Math.floor(memberCount / 1000)}k` : memberCount}
          </span>
        ) : null}
        <ChevronRight size={20} className="text-surface-700 flex-shrink-0" />
      </button>
      <div className="an-divider-inset" />
    </>
  );
}

/* ── Таб «Друзья» — FriendsTab из ChannelListScreen.kt ──────────────────── */

function FriendsTab({ onOpen }: { onOpen: (path: string) => void }) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');

  const { data: friends, isLoading } = useQuery({
    queryKey: ['friends'],
    queryFn: () => friendsApi.list(),
  });
  const { data: pendingRequests } = useQuery({
    queryKey: ['friends', 'pending'],
    queryFn: () => friendsApi.listPending(),
    staleTime: 30_000,
  });
  const { data: myBots } = useQuery({
    queryKey: ['my-bots'],
    queryFn: () => botsApi.list(),
    staleTime: 60_000,
  });

  const acceptMut = useMutation({
    mutationFn: (id: string) => friendsApi.accept(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['friends'] }),
  });
  const rejectMut = useMutation({
    mutationFn: (id: string) => friendsApi.reject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['friends'] }),
  });

  const q = search.toLowerCase();
  const visibleFriends = (friends ?? []).filter(
    (f: any) => !q || (f.friend.displayName ?? '').toLowerCase().includes(q) || (f.friend.username ?? '').toLowerCase().includes(q),
  );
  const visibleBots = (myBots ?? []).filter(
    (b: any) => !q || (b.displayName ?? '').toLowerCase().includes(q) || (b.username ?? '').toLowerCase().includes(q),
  );

  return (
    <div className="pb-8">
      {/* Поиск друзей */}
      <div className="px-3 py-2">
        <div className="flex items-center gap-2 bg-surface-900 border border-surface-800 focus-within:border-brand-500 rounded-xl px-3 py-2.5 transition-colors">
          <Search size={18} className="text-surface-400 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск друзей…"
            className="bg-transparent text-sm text-surface-50 outline-none w-full placeholder:text-surface-400"
          />
          {search.length > 0 && (
            <button onClick={() => setSearch('')} className="text-surface-400" aria-label="Очистить">
              <X size={18} />
            </button>
          )}
        </div>
      </div>

      <button
        type="button"
        className="an-list-row"
        style={{ minHeight: 60 }}
        onClick={() => onOpen('/friends/requests')}
      >
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-900/40 text-brand-300">
          <UserPlus size={20} />
        </span>
        <span className="min-w-0 flex-1 text-left">
          <span className="block text-[15px] font-semibold text-surface-50">Заявки в друзья</span>
          <span className="block text-xs text-surface-400">Входящие и исходящие</span>
        </span>
        {pendingRequests && pendingRequests.length > 0 && (
          <span className="an-badge">{pendingRequests.length > 99 ? '99+' : pendingRequests.length}</span>
        )}
        <ChevronRight size={20} className="flex-shrink-0 text-surface-700" />
      </button>
      <div className="an-divider-inset" />

      {/* Входящие заявки: имя + ✓(зелёный)/✕(красный) — PendingRequestItem */}
      {pendingRequests && pendingRequests.length > 0 && (
        <>
          <p className="px-4 pt-2 pb-1 text-xs uppercase tracking-wider text-surface-400 flex items-center gap-1.5">
            <UserPlus size={14} className="text-brand-600" style={{ color: '#7c3aed' }} />
            Заявки ({pendingRequests.length})
          </p>
          {pendingRequests.map((req: any) => (
            <div key={req.id} className="flex items-center gap-3 px-4 py-2">
              <div className="an-avatar an-avatar--40">
                {(req.sender.username ?? '?').slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-surface-50 truncate">
                  <NickName user={req.sender} />
                </p>
                <p className="text-xs text-surface-400 truncate">@{req.sender.username}</p>
              </div>
              <button
                onClick={() => acceptMut.mutate(req.id)}
                className="w-8 h-8 flex items-center justify-center"
                style={{ color: '#4ade80' }}
                aria-label="Принять"
              >
                <Check size={20} />
              </button>
              <button
                onClick={() => rejectMut.mutate(req.id)}
                className="w-8 h-8 flex items-center justify-center"
                style={{ color: '#ef4444' }}
                aria-label="Отклонить"
              >
                <X size={20} />
              </button>
            </div>
          ))}
          <div className="an-divider-inset" style={{ marginLeft: 74 }} />
        </>
      )}

      {isLoading && <p className="px-4 py-6 text-surface-400 text-sm text-center">Загрузка…</p>}

      {/* Друзья: FriendItem — аватар 44dp + presence, чат-иконка справа */}
      {visibleFriends.map((f: any) => (
        <button
          key={f.friendshipId}
          className="an-list-row"
          style={{ minHeight: 68 }}
          onClick={() => onOpen(`/dm/${f.friend.id}`)}
        >
          <div className="relative flex-shrink-0">
            <Avatar src={f.friend.avatarUrl} username={f.friend.username} size="md" />
            <span
              className={clsx(
                'an-presence',
                f.friend.status === 'ONLINE' && 'an-presence--online',
                f.friend.status === 'AWAY' && 'an-presence--away',
                f.friend.status === 'DND' && 'an-presence--dnd',
                (!f.friend.status || f.friend.status === 'OFFLINE') && 'an-presence--offline',
              )}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-semibold text-surface-50 truncate">
              <NickName user={f.friend} />
            </p>
            <p className="text-xs text-surface-400 truncate">@{f.friend.username}</p>
          </div>
          <MessageCircle size={18} className="text-surface-400 flex-shrink-0" />
        </button>
      ))}

      {/* Мои боты — тоже диалоги (web-паритет Phase 26.1) */}
      {visibleBots.length > 0 && (
        <>
          <p className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider text-surface-400">Мои боты</p>
          {visibleBots.map((bot: any) => (
            <button key={bot.id} className="an-list-row" onClick={() => onOpen(`/dm/${bot.userId}`)}>
              <Avatar src={bot.avatarUrl} username={bot.username} size="md" />
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-surface-50 truncate">{bot.displayName}</p>
                <p className="text-xs text-surface-400 truncate">@{bot.username}</p>
              </div>
              <MessageCircle size={18} className="text-surface-400 flex-shrink-0" />
            </button>
          ))}
        </>
      )}

      {!isLoading && visibleFriends.length === 0 && visibleBots.length === 0 && !pendingRequests?.length && (
        <p className="px-4 py-8 text-surface-400 text-sm text-center">
          Пока пусто. Найдите людей через поиск 🔍 наверху.
        </p>
      )}
    </div>
  );
}
